import type * as Monaco from "monaco-editor";

export const PINE_LANGUAGE_ID = "pinescript";

const KEYWORDS = [
  "if",
  "else",
  "switch",
  "for",
  "while",
  "once",
  "continue",
  "break",
  "to",
  "by",
  "in",
  "and",
  "or",
  "not",
  "var",
  "varip",
  "export",
  "method",
  "type",
  "enum",
  "import",
  "as",
  "series",
  "simple",
  "const",
  "input",
];

const TYPES = [
  "int",
  "float",
  "bool",
  "string",
  "color",
  "line",
  "label",
  "box",
  "table",
  "linefill",
  "polyline",
  "array",
  "matrix",
  "map",
  "footprint",
  "volume_row",
];

const CONSTANTS = ["true", "false", "na"];

/** Common builtins / declarations (subset; enough for readable highlighting). */
const BUILTINS = [
  "indicator",
  "strategy",
  "library",
  "plot",
  "plotshape",
  "plotchar",
  "plotcandle",
  "plotbar",
  "plotarrow",
  "hline",
  "fill",
  "bgcolor",
  "barcolor",
  "alert",
  "alertcondition",
  "nz",
  "fixnan",
  "max_bars_back",
  "time",
  "time_close",
  "timestamp",
  "year",
  "month",
  "weekofyear",
  "dayofmonth",
  "dayofweek",
  "hour",
  "minute",
  "second",
  "open",
  "high",
  "low",
  "close",
  "volume",
  "hl2",
  "hlc3",
  "ohlc4",
  "bar_index",
  "barstate",
  "syminfo",
  "timeframe",
  "ticker",
  "tickerid",
];

const NAMESPACES = [
  "ta",
  "math",
  "str",
  "array",
  "matrix",
  "map",
  "color",
  "line",
  "label",
  "box",
  "table",
  "input",
  "request",
  "runtime",
  "log",
  "strategy",
  "chart",
];

function pineLanguage(): Monaco.languages.IMonarchLanguage {
  return {
    defaultToken: "",
    tokenPostfix: ".pine",
    keywords: KEYWORDS,
    typeKeywords: TYPES,
    constants: CONSTANTS,
    builtins: BUILTINS,
    namespaces: NAMESPACES,
    operators: [
      "=",
      ":=",
      "==",
      "!=",
      ">",
      ">=",
      "<",
      "<=",
      "+",
      "-",
      "*",
      "/",
      "%",
      "?",
      ":",
      "=>",
      "[",
      "]",
    ],
    symbols: /[=><!~?:&|+\-*/%^]+/,
    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
    tokenizer: {
      root: [
        [/\/\/.*/, "comment"],
        [/\/\*/, "comment", "@comment"],
        [/"([^"\\]|\\.)*$/, "string.invalid"],
        [/'([^'\\]|\\.)*$/, "string.invalid"],
        [/"/, "string", "@string_double"],
        [/'/, "string", "@string_single"],
        [/#(?:[0-9a-fA-F]{8}|[0-9a-fA-F]{6}|[0-9a-fA-F]{3})\b/, "number.hex"],
        [/\d*\.\d+([eE][-+]?\d+)?/, "number.float"],
        [/\d+/, "number"],
        [
          /[a-zA-Z_][\w.]*/,
          {
            cases: {
              "@keywords": "keyword",
              "@typeKeywords": "type",
              "@constants": "constant",
              "@builtins": "predefined",
              "@namespaces": "namespace",
              "@default": "identifier",
            },
          },
        ],
        [/[{}()[\]]/, "@brackets"],
        [/[;,.]/, "delimiter"],
        [
          /@symbols/,
          {
            cases: {
              "@operators": "operator",
              "@default": "",
            },
          },
        ],
      ],
      comment: [
        [/[^/*]+/, "comment"],
        [/\*\//, "comment", "@pop"],
        [/./, "comment"],
      ],
      string_double: [
        [/[^\\"]+/, "string"],
        [/@escapes/, "string.escape"],
        [/\\./, "string.escape.invalid"],
        [/"/, "string", "@pop"],
      ],
      string_single: [
        [/[^\\']+/, "string"],
        [/@escapes/, "string.escape"],
        [/\\./, "string.escape.invalid"],
        [/'/, "string", "@pop"],
      ],
    },
  };
}

const THEME_DARK = "pine-dark-plus";
const THEME_LIGHT = "pine-light";

let registered = false;

export function registerPineLanguage(monaco: typeof Monaco) {
  if (registered) return;
  registered = true;

  monaco.languages.register({ id: PINE_LANGUAGE_ID });
  monaco.languages.setMonarchTokensProvider(PINE_LANGUAGE_ID, pineLanguage());
  monaco.languages.setLanguageConfiguration(PINE_LANGUAGE_ID, {
    comments: {
      lineComment: "//",
      blockComment: ["/*", "*/"],
    },
    brackets: [
      ["{", "}"],
      ["[", "]"],
      ["(", ")"],
    ],
    autoClosingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
    surroundingPairs: [
      { open: "{", close: "}" },
      { open: "[", close: "]" },
      { open: "(", close: ")" },
      { open: '"', close: '"' },
      { open: "'", close: "'" },
    ],
  });

  // Design A editor themes (dark and light).
  // Design A palette: app surfaces, teal cursor/selection, purple keywords,
  // blue functions, green strings, amber numbers, muted comments.
  monaco.editor.defineTheme(THEME_DARK, {
    base: "vs-dark",
    inherit: true,
    rules: [
      { token: "comment.pine", foreground: "64748B", fontStyle: "italic" },
      { token: "string.pine", foreground: "22C55E" },
      { token: "string.escape.pine", foreground: "22C55E" },
      { token: "number.pine", foreground: "F59E0B" },
      { token: "number.float.pine", foreground: "F59E0B" },
      { token: "number.hex.pine", foreground: "F59E0B" },
      { token: "keyword.pine", foreground: "C084FC" },
      { token: "operator.pine", foreground: "94A3B8" },
      { token: "type.pine", foreground: "00BFA6" },
      { token: "constant.pine", foreground: "C084FC" },
      { token: "predefined.pine", foreground: "60A5FA" },
      { token: "namespace.pine", foreground: "00BFA6" },
      { token: "identifier.pine", foreground: "CBD5E1" },
      { token: "delimiter.pine", foreground: "94A3B8" },
    ],
    colors: {
      "editor.background": "#111827",
      "editor.foreground": "#CBD5E1",
      "editorLineNumber.foreground": "#64748B",
      "editorLineNumber.activeForeground": "#CBD5E1",
      "editor.selectionBackground": "#00BFA633",
      "editor.selectionHighlightBackground": "#00BFA61F",
      "editor.lineHighlightBackground": "#1B2435",
      "editor.lineHighlightBorder": "#1B2435",
      "editorCursor.foreground": "#00BFA6",
      "editorIndentGuide.background": "#1F2937",
      "editorIndentGuide.activeBackground": "#64748B",
      "editorWidget.background": "#161E2E",
      "editorWidget.border": "#273449",
      "input.background": "#111827",
      "input.foreground": "#CBD5E1",
      "dropdown.background": "#161E2E",
      "dropdown.foreground": "#CBD5E1",
      "list.hoverBackground": "#1B2435",
      "list.activeSelectionBackground": "#00BFA633",
    },
  });

  monaco.editor.defineTheme(THEME_LIGHT, {
    base: "vs",
    inherit: true,
    rules: [
      { token: "comment.pine", foreground: "9CA3AF", fontStyle: "italic" },
      { token: "string.pine", foreground: "16A34A" },
      { token: "string.escape.pine", foreground: "16A34A" },
      { token: "number.pine", foreground: "D97706" },
      { token: "number.float.pine", foreground: "D97706" },
      { token: "number.hex.pine", foreground: "D97706" },
      { token: "keyword.pine", foreground: "9333EA" },
      { token: "operator.pine", foreground: "6B7280" },
      { token: "type.pine", foreground: "00A894" },
      { token: "constant.pine", foreground: "9333EA" },
      { token: "predefined.pine", foreground: "2563EB" },
      { token: "namespace.pine", foreground: "00A894" },
      { token: "identifier.pine", foreground: "374151" },
      { token: "delimiter.pine", foreground: "6B7280" },
    ],
    colors: {
      "editor.background": "#FFFFFF",
      "editor.foreground": "#374151",
      "editorLineNumber.foreground": "#9CA3AF",
      "editorLineNumber.activeForeground": "#374151",
      "editor.selectionBackground": "#00A89433",
      "editor.selectionHighlightBackground": "#00A8941F",
      "editor.lineHighlightBackground": "#F8FAFC",
      "editor.lineHighlightBorder": "#F8FAFC",
      "editorCursor.foreground": "#00A894",
      "editorIndentGuide.background": "#E5E7EB",
      "editorIndentGuide.activeBackground": "#9CA3AF",
      "editorWidget.background": "#FFFFFF",
      "editorWidget.border": "#D9DEE5",
      "input.background": "#FFFFFF",
      "input.foreground": "#374151",
      "dropdown.background": "#FFFFFF",
      "dropdown.foreground": "#374151",
      "list.hoverBackground": "#F8FAFC",
      "list.activeSelectionBackground": "#00A89433",
    },
  });
}

/** Monaco theme id for the current app theme (Design A dark / light). */
export function pineEditorTheme(appTheme: "dark" | "light" = "dark") {
  return appTheme === "light" ? THEME_LIGHT : THEME_DARK;
}

export function pineEditorOptions(
  extras: Monaco.editor.IStandaloneEditorConstructionOptions = {}
): Monaco.editor.IStandaloneEditorConstructionOptions {
  return {
    language: PINE_LANGUAGE_ID,
    theme: THEME_DARK,
    fontSize: 12.5,
    lineHeight: 21,
    fontFamily: '"Geist Mono", ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    automaticLayout: true,
    wordWrap: "off",
    tabSize: 4,
    renderLineHighlight: "line",
    padding: { top: 8, bottom: 8 },
    ...extras,
  };
}
